import React, { useCallback, useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { Transition } from '@headlessui/react';
import { Head, Link, router, usePage } from '@inertiajs/react';
import numbro from 'numbro';
import styled from 'styled-components';
import classNames from 'classnames';
import { dateFormat, useTranslations } from '@/app/i18n.js';
import { UTCDate } from '@date-fns/utc';
import { endOfDay } from 'date-fns';
import { resolveActivePromo } from '@/app/utils/gamePromo.js';
import GTMEventManager from '@/app/GTMEventManager.js';

// components
import ThemeToggle from '@/app/Components/ThemeToggle.jsx';
import ApplicationLogo from '@/app/Components/ApplicationLogo.jsx';
import PrimaryButton from '@/app/Components/PrimaryButton.jsx';
import ActionButton from '@/app/Components/ActionButton.jsx';
import AlertCountdown from '@/app/Components/AlertCountdown.jsx';
// import LanguageSwitcher from '@/app/Layouts/Partials/LanguageSwitcher.jsx';
// import UserMenu from '@/app/Layouts/Partials/UserMenu.jsx';
// partials
import HelpPopup from '@/app/Pages/Booking/Partials/HelpPopup.jsx';
import DartsPromoPopup from '@/app/Pages/Booking/Partials/DartsPromoPopup.jsx';
import AsideMenu from '@/app/Pages/Booking/Partials/AsideMenu.jsx';
import LocationStep from '@/app/Pages/Booking/Partials/LocationStep.jsx';
// import CategoryStep from '@/app/Pages/Booking/Partials/CategoryStep.jsx';
import GameStep from '@/app/Pages/Booking/Partials/GameStep.jsx';
import TeamStep from '@/app/Pages/Booking/Partials/TeamStep.jsx';
import { updateBookingSteps, selectAlternativeSteps } from '@/app/Pages/Booking/Partials/bookingAlternatives.js';
import DateStep from '@/app/Pages/Booking/Partials/DateStep.jsx';
import Footer from '@/app/Pages/Booking/Partials/Footer.jsx';
import Alert from '@/app/Components/Alert.jsx';
// assets
import CheckIconSvg from '@assets/icons/check-outline-2.svg?react';
import BeachUmbrellaIcon from '@assets/icons/beach-with-umbrella.svg?react';

const Container = styled.section`
    width: 100%;
    @media (min-width: 1024px) and (max-width: 1280px) {
        width: calc(100% - 22rem);
    }
    @media (min-width: 1280px) and (max-width: 1920px) {
        width: calc(100% - 26rem);
    }
    @media (min-width: 1920px) {
        width: calc(100% - 30rem);
    }
    body.dark & {
        @media (min-width: 1024px) and (max-width: 1280px) {
            width: calc(100% - 18rem);
        }
        @media (min-width: 1280px) and (max-width: 1920px) {
            width: calc(100% - 20rem);
        }
        @media (min-width: 1920px) {
            width: calc(100% - 26rem);
        }
    }
`;

const Summary = styled.aside`
    width: 21rem;
    @media (min-width: 1024px) and (max-width: 1280px) {
        width: 18rem;
    }
    @media (min-width: 1920px) {
        width: 26rem;
    }
    @media (min-width: 1280px) and (max-width: 1920px) {
        width: 20rem;
    }

    .continue-container {
        width: 21rem;
        @media (min-width: 1024px) and (max-width: 1280px) {
            width: 18rem;
        }
        @media (min-width: 1280px) and (max-width: 1920px) {
            width: 20rem;
        }
        @media (min-width: 1920px) {
            width: 26rem;
        }
    }

    body:not(.dark) & {
        min-width: 0;
        width: 22rem;
        @media (min-width: 1024px) and (max-width: 1280px) {
            min-width: 22rem;
            width: 22rem;
        }
        @media (min-width: 1280px) and (max-width: 1920px) {
            min-width: 26rem;
            width: 26rem;
        }
        @media (min-width: 1920px) {
            min-width: 30rem;
            width: 30rem;
        }

        .continue-container {
            width: auto;
            margin-top: 1.8rem;
        }
    }
`;

const SummaryMobile = styled.section`
    width: 100%;
    position: fixed;
    bottom: 0;
`;

const STEPS = {
    location: 'LOCATION',
    // category: 'CATEGORY',
    game: 'GAME',
    team: 'TEAM',
    date: 'DATE',
};

function BookingIndex({ auth, settings, points_of_sale, selected_point_of_sale, categories, maximum_players, games, selected_game, selected_team, slots, selected_slot, planning_days, pricing_exceptions }) {
    const page = usePage();
    const __ = useTranslations();
    const locale = page.props.language;
    const cartError = page.props.errors?.cart;

    const [gamesLoading, setGamesLoading] = useState(false);
    const [gamesLoaded, setGamesLoaded] = useState(games && games.length > 0);
    const [selectionLoading, setSelectionLoading] = useState(false);
    const [selectionError, setSelectionError] = useState(false);
    const selectionRequest = useRef(0);
    const alternativeFocus = useRef(null);
    const [cartLoading, setCartLoading] = useState(false);

    // const [summaryOpened, setSummaryOpened] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    const [currentStep, setCurrentStep] = useState(selected_slot || selected_game ? selected_team ? STEPS.date : STEPS.team : selected_point_of_sale ? STEPS.game : STEPS.location);

    const [hint, setHint] = useState(null);
    const [showHint, setShowHint] = useState(false);
    const [showHelpNeededHint, setShowHelpNeededHint] = useState(false);
    // const [showDartsPromo, setShowDartsPromo] = useState(() => {
    //     try {
    //         return localStorage.getItem('darts_promo_dismissed') !== 'true';
    //     } catch {
    //         return true;
    //     }
    // });
    const [clickCount, setClickCount] = useState(0);
    const hintTimeoutRef = useRef(null);
    const helpHintTimeoutRef = useRef(null);
    // console.log('cartError', cartError);
    // const [selectedCategory, setSelectedCategory] = useState(null);

    const [steps, setSteps] = useState({
        LOCATION: {
            title: __('book.index.steps.location.title'),
            menu: __('book.index.steps.location.menu'),
            internal: selected_point_of_sale,
            value: {
                title: selected_point_of_sale
                    ? `${selected_point_of_sale.address.split(',').shift()}\n${selected_point_of_sale.address.split(',').slice(1).join(', ')}`
                    : null,
                // subtitle: selected_point_of_sale?.phone_number ?? null,
                subtitle: ' ',
            }
        },
        // CATEGORY: {
        //     title: __('book.index.steps.category.title'),
        //     menu: __('book.index.steps.category.menu'),
        //     internal: selectedCategory,
        //     value: {
        //         title: selectedCategory?.title_1 ? `${selectedCategory?.title_1[locale]} ${selectedCategory?.title_2[locale]}` : null,
        //         subtitle: selectedCategory?.subtitle[locale] ?? null,
        //     }
        // },
        GAME: {
            title: __('book.index.steps.game.title'),
            menu: __('book.index.steps.game.menu'),
            internal: selected_game
                ? {
                    ...selected_game,
                    promo: selected_game.promo ?? games?.find((g) => g.id === selected_game.id || g.slug === selected_game.slug)?.promo ?? null,
                }
                : null,
            value: {
                title: selected_game?.name?.split(':')[0] ?? null,
                subtitle: selected_game?.catch_phrase[locale] ?? null,
            }
        },
        TEAM: {
            title: __('book.index.steps.team.title'),
            menu: __('book.index.steps.team.menu'),
            internal: selected_team,
            value: {
                title: selected_team ? __('book.index.steps.team.value_title', { players: selected_team }) : null,
                subtitle: null,
            }
        },
        DATE: {
            title: __('book.index.steps.date.title'),
            menu: __('book.index.steps.date.menu'),
            internal: selected_slot,
            value: {
                title: selected_slot ? dateFormat(new UTCDate(selected_slot.date), 'EEEE dd MMMM yyyy') : null,
                subtitle: selected_slot ? selected_slot.time : null,
            }
        },
    });

    const verifyingEmail = useMemo(() => {
        const query = new URLSearchParams(window.location.search);
        return query.has('verified');
    }, []);

    const stepsComplete = useMemo(() => {
        return !selectionLoading && steps.GAME.internal && steps.TEAM.internal && steps.DATE?.internal?.time && steps.DATE?.internal?.time?.trim() !== ''
    }, [steps, selectionLoading]);

    const setStepValue = useCallback((stepKey, internal, value) => {
        setSteps((prev) => updateBookingSteps(prev, stepKey, internal, value));

        setShowHelpNeededHint(false);
        setShowHint(false);
        setClickCount(0);
    }, [steps]);

    useEffect(() => {
        setSteps((previous) => {
            const sameContext = previous.LOCATION.internal?.id === selected_point_of_sale?.id
                && previous.GAME.internal?.id === selected_game?.id
                && Number(previous.TEAM.internal) === Number(selected_team);
            const date = selected_slot || (sameContext && previous.DATE.internal?.date
                ? { date: previous.DATE.internal.date, time: ' ', slot: null } : null);
            return {
                ...previous,
                LOCATION: { ...previous.LOCATION, internal: selected_point_of_sale, value: { title: selected_point_of_sale ? `${selected_point_of_sale.address.split(',').shift()}\n${selected_point_of_sale.address.split(',').slice(1).join(', ')}` : null, subtitle: ' ' } },
                GAME: { ...previous.GAME, internal: selected_game, value: { title: selected_game?.name?.split(':')[0] ?? null, subtitle: selected_game?.catch_phrase?.[locale] ?? null } },
                TEAM: { ...previous.TEAM, internal: selected_team, value: { title: selected_team, subtitle: ' ' } },
                DATE: { ...previous.DATE, internal: date, value: { title: date?.date ? dateFormat(new UTCDate(date.date), 'EEEE dd MMMM yyyy') : null, subtitle: date?.time ?? null } },
            };
        });
        setCurrentStep(selected_game ? selected_team ? STEPS.date : STEPS.team : selected_point_of_sale ? STEPS.game : STEPS.location);
    }, [selected_point_of_sale, selected_game, selected_team, selected_slot]);

    useEffect(() => {
        if (!selectionLoading && alternativeFocus.current === selected_game?.id) {
            document.getElementById('booking-times-title')?.focus({ preventScroll: true });
            alternativeFocus.current = null;
        }
    }, [selectionLoading, selected_game]);

    const selectAlternative = (game, choice) => {
        const request = ++selectionRequest.current;
        const previous = steps;
        let accepted = false;
        setSteps(selectAlternativeSteps(steps, game, choice, locale, dateFormat(new UTCDate(choice.date), 'EEEE dd MMMM yyyy')));
        alternativeFocus.current = game.id;
        setSelectionLoading(true);
        setSelectionError(false);
        const failed = () => {
            if (selectionRequest.current !== request) return;
            alternativeFocus.current = null;
            setSteps(previous);
            setSelectionError(true);
        };
        // The existing deep-link contract revalidates the arena and refreshes the game's calendar.
        router.get(route('booking.slots', { point_of_sale_slug: steps.LOCATION.internal.slug, game: game.slug }), {
            p: steps.TEAM.internal,
            shared_arena: choice.shared_arena_id,
            t: Math.floor(new Date(`${choice.date}T${choice.time}:00Z`).getTime() / 1000),
        }, {
            preserveState: true, preserveScroll: true,
            onSuccess: (page) => {
                accepted = page.props.selected_game?.id === game.id;
                if (selectionRequest.current === request && accepted && !page.props.selected_slot?.slot) setSelectionError(true);
            },
            onError: failed, onCancel: failed,
            onFinish: () => {
                if (selectionRequest.current !== request) return;
                if (!accepted) failed();
                setSelectionLoading(false);
            },
        });
    };

    useEffect(() => {
        if (!selected_game?.promo) {
            return;
        }

        setSteps((prev) => {
            const internal = prev.GAME.internal;

            if (!internal || (internal.id !== selected_game.id && internal.slug !== selected_game.slug)) {
                return prev;
            }

            if (internal.promo) {
                return prev;
            }

            return {
                ...prev,
                GAME: {
                    ...prev.GAME,
                    internal: {
                        ...internal,
                        promo: selected_game.promo,
                    },
                },
            };
        });
    }, [selected_game]);

    const submitStep = useCallback((stepValue) => {
        const query = new URLSearchParams(window.location.search);

        if (! currentStep || (! stepValue && currentStep !== STEPS.category)) {
            return;
        }

        selectionRequest.current += 1;
        alternativeFocus.current = null;
        setSelectionLoading(false);
        setSelectionError(false);
        switch (currentStep) {
            case STEPS.location:
                query.delete('t');
                query.delete('shared_arena');
                const locationChanged = stepValue.slug !== steps.LOCATION.internal?.slug;
                setStepValue(currentStep, stepValue, {
                    title: `${stepValue.address.split(',').shift()}\n${stepValue.address.split(',').slice(1).join(', ')}`,
                    subtitle: ' ',
                });

                setCurrentStep(STEPS.game);

                if (locationChanged) {
                    // Will load all available games for the selection point of sale
                    setGamesLoaded(false);
                    setGamesLoading(true);
                    router.visit(
                        route('booking.game', { point_of_sale_slug: stepValue.slug }),
                        {
                            method: 'get',
                            replace: true,
                            preserveScroll: false,
                            preserveState: true,
                            onSuccess: () => {
                                setGamesLoaded(true);
                                setGamesLoading(false);
                            }
                        },
                    );
                }
                break;
            // case STEPS.category:
            //     if (stepValue === null) {
            //         setCurrentStep(STEPS.game);
            //     } else {
            //         if (stepValue.featured_game) {
            //             window.scrollTo({left: 0, top: 0});
            //             setStepValue(STEPS.game, stepValue.featured_game, {
            //                 title: stepValue.featured_game.name.split(':')[0],
            //                 subtitle: stepValue.featured_game.catch_phrase[locale],
            //             });
            //             GTMEventManager.ecommerce().selectItem(stepValue.featured_game);
            //
            //             setSelectedCategory(null);
            //
            //             if (query.has('p')) {
            //                 const team = parseInt(query.get('p'));
            //                 if (!isNaN(team) && stepValue.featured_game.min_players <= team && stepValue.featured_game.max_players >= team) {
            //                     setCurrentStep(STEPS.date);
            //                 } else {
            //                     query.delete('p');
            //                     setCurrentStep(STEPS.team);
            //                 }
            //             } else {
            //                 setCurrentStep(STEPS.team);
            //             }
            //             router.visit(
            //                 route('booking.slots', { point_of_sale_slug: steps.LOCATION.internal?.slug ?? selected_point_of_sale.slug, game: stepValue.featured_game.slug }) + '?' + query.toString(),
            //                 {
            //                     method: 'get',
            //                     preserveState: true,
            //                     preserveScroll: false,
            //                     onSuccess: () => {
            //                         GTMEventManager.ecommerce().customizeProduct(stepValue, {
            //                             context: 'funnel',
            //                             item_key: 'game',
            //                             item_value: stepValue.featured_game.slug
            //                         });
            //                     }
            //                 }
            //             );
            //         } else {
            //             setSelectedCategory(stepValue);
            //             setStepValue(currentStep, stepValue, {
            //                 title: `${stepValue.title_1[locale]} ${stepValue.title_2[locale]}`,
            //                 subtitle: stepValue.subtitle[locale],
            //             });
            //             setCurrentStep(STEPS.game);
            //         }
            //     }
            //
            //     window.scrollTo({left: 0, top: 0});
            //     break;
            case STEPS.game:
                query.delete('t');
                query.delete('shared_arena');
                window.scrollTo({left: 0, top: 0});
                const gameFromList = games?.find((g) => g.id === stepValue.id || g.slug === stepValue.slug);
                const gameWithPromo = gameFromList
                    ? { ...stepValue, promo: stepValue.promo ?? gameFromList.promo }
                    : stepValue;
                setStepValue(currentStep, gameWithPromo, {
                    title: stepValue.name.split(':')[0],
                    subtitle: stepValue.catch_phrase[locale],
                });
                GTMEventManager.ecommerce().selectItem(stepValue);

                // setSelectedCategory(null);

                if (query.has('p')) {
                    const team = parseInt(query.get('p'));
                    if (!isNaN(team) && stepValue.min_players <= team && stepValue.max_players >= team) {
                        setCurrentStep(STEPS.date);
                    } else {
                        query.delete('p');
                        setCurrentStep(STEPS.team);
                    }
                } else {
                    setCurrentStep(STEPS.team);
                }
                router.visit(
                    route('booking.slots', { point_of_sale_slug: steps.LOCATION.internal?.slug ?? selected_point_of_sale.slug, game: stepValue.slug }) + '?' + query.toString(),
                    {
                        method: 'get',
                        preserveState: true,
                        preserveScroll: false,
                        onSuccess: () => {
                            GTMEventManager.ecommerce().customizeProduct(stepValue, {
                                context: 'funnel',
                                item_key: 'game',
                                item_value: stepValue.slug
                            });
                        }
                    }
                );
                break;
            case STEPS.team:
                setStepValue(STEPS.team, stepValue, { title: stepValue, subtitle: ' ' });
                query.delete('t');
                query.delete('shared_arena');
                query.set('p', stepValue);
                router.visit(
                    route('booking.slots', { point_of_sale_slug: selected_point_of_sale.slug, game: steps.GAME.internal?.slug ?? selected_game?.slug }) + '?' + query.toString(),
                    {
                        method: 'get',
                        preserveState: true,
                        preserveScroll: false,
                        onSuccess: () => {
                            GTMEventManager.ecommerce().customizeProduct(steps.GAME.internal, {
                                context: 'funnel',
                                item_key: 'team',
                                item_value: stepValue
                            });
                        }
                    }
                );
                break;
            case STEPS.date:
                setStepValue(currentStep, stepValue, {
                    title: stepValue.date ? dateFormat(new UTCDate(stepValue.date), 'EEEE dd MMMM yyyy') : null,
                    subtitle: stepValue.time,
                });
                if (stepValue?.slot?.price) {
                    GTMEventManager.ecommerce().customizeProduct(steps.GAME.internal, {
                        context: 'funnel',
                        item_key: 'slot',
                        item_value: `${stepValue.date} ${stepValue.time}`.trim(),
                        value: Math.ceil((stepValue.slot.price * steps.TEAM.internal)) / 100,
                    });
                }
                // if (stepValue.date && stepValue.time && stepValue.time.trim() !== '') {
                //     query.set('t', getUnixTime(`${stepValue.date} ${stepValue.time}`).toString());
                // }
                break;
        }

        if (helpHintTimeoutRef.current) {
            clearTimeout(helpHintTimeoutRef.current);
        }

        helpHintTimeoutRef.current = helpTimeout();

    }, [currentStep, games, locale, selected_point_of_sale, steps]);

    const createBooking = useCallback((e) => {
        GTMEventManager.clickButton({
            context: 'summary',
            text: e?.target?.innerText ?? '',
            behavior: 'submit',
        });
        const existing = page.props.existing_cart;
        const replacing = existing && existing.id !== selected_point_of_sale.id;
        if (replacing && !window.confirm(`Votre panier concerne ${existing.name}. Le remplacer pour ${selected_point_of_sale.name} ? Les expériences, créneaux et promotions du panier seront retirés.`)) return;
        setCartLoading(true);
        router.post(route('booking.store', { point_of_sale_slug: selected_point_of_sale.slug, game: steps.GAME.internal?.slug ?? selected_game?.slug}), {
            replace_cart: !!replacing,
            team: steps.TEAM.internal ?? selected_team,
            slot: steps.DATE.internal ?? selected_slot,
        }, {
            onFinish: () => {
                setCartLoading(false);
            },
            onSuccess: () => {
                document.body.classList.remove('overflow-hidden'); // ensure the next page is scrollable
                const team = parseInt(steps.TEAM.internal ?? selected_team);
                const game = steps.GAME.internal ?? selected_game;
                const slot = steps.DATE.internal ?? selected_slot;

                GTMEventManager.ecommerce().addToCart(game, team, slot.slot.price, steps.LOCATION.internal, {
                    privatized: false,
                    booking_datetime: `${dateFormat(slot.slot.start, 'yyyy-MM-dd\'T\'HH:mm:ss')}.000000Z`,
                })
            }
        });

        return () => {
            setCartLoading(false);
        }
    }, [cartLoading, steps.TEAM.internal, steps.DATE.internal, steps.GAME.internal]);

    const HelpHints = {
        help_needed: __('book.cart.hints.help_needed'),
        game_missing: __('book.cart.hints.game_missing'),
        team_missing: __('book.cart.hints.team_missing'),
        date_missing: __('book.cart.hints.date_missing'),
        slot_missing: __('book.cart.hints.slot_missing'),
    };

    const handleClickContinue = useCallback((e) => {
        if (cartLoading || selectionLoading || showHelpNeededHint) {
            return;
        }

        // Increment the click count
        setClickCount((prev) => {
            const newCount = prev + 1;

            if (newCount === 3) {
                // Clear any existing timeout to prevent premature closing
                if (hintTimeoutRef.current) {
                    clearTimeout(hintTimeoutRef.current);
                }

                GTMEventManager.clickButton({
                    context: 'summary',
                    text: e.target.innerText,
                    behavior: 'show_help_popup',
                });

                // Display the special hint if clicked 3 times rapidly
                // Close the alert if it's currently shown, then reopen it
                setShowHint(false);
                setTimeout(() => {
                    setShowHelpNeededHint(true);
                    setHint({
                        title: HelpHints.help_needed,
                        icon: (
                            <svg className="size-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
                                <rect width="256" height="256" fill="none"/>
                                <circle cx="128" cy="180" r="12"/>
                                <path d="M128,144v-8c17.67,0,32-12.54,32-28s-14.33-28-32-28S96,92.54,96,108v4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>
                                <circle cx="128" cy="128" r="96" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>
                            </svg>
                        ),
                    });

                    setShowHint(true);
                }, 200);


                // Auto-hide the special hint after 10 seconds
                setTimeout(() => {
                    setShowHelpNeededHint(false);
                    setShowHint(false);
                    setClickCount(0);
                }, 10000);

                return 0; // Reset click count after showing special hint
            }

            return newCount;
        });

        if (clickCount >= 2 || showHelpNeededHint) {
            GTMEventManager.clickButton({
                context: 'summary',
                text: e.target.innerText,
                behavior: 'abort',
            });

            return; // Stop execution to prevent showing normal hints when special hint is active
        }

        const displayHint = (newHint, timeout = 3000) => {
            // Clear any existing timeout to prevent premature closing
            if (hintTimeoutRef.current) {
                clearTimeout(hintTimeoutRef.current);
            }
            if (helpHintTimeoutRef.current) {
                clearTimeout(helpHintTimeoutRef.current);
            }

            GTMEventManager.clickButton({
                context: 'summary',
                text: e.target.innerText,
                behavior: 'show_hint',
                value: newHint.title,
            });

            // Close the alert if it's currently shown, then reopen it
            setShowHint(false);
            setTimeout(() => {
                setHint(newHint);
                setShowHint(true);

                // Set a new timeout for auto-closing
                hintTimeoutRef.current = setTimeout(() => {
                    setShowHint(false);
                    hintTimeoutRef.current = null; // Clear the timeout reference
                }, timeout);
            }, 200); // Slight delay for close animation
            helpHintTimeoutRef.current = helpTimeout();
        };

        if (! steps.GAME.internal) {
            displayHint({ title: HelpHints.game_missing, icon: 'default' });
            return;
        }

        if (! steps.TEAM.internal) {
            displayHint({ title: HelpHints.team_missing, icon: 'default' });
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
            return;
        }

        if (! steps.DATE.internal) {
            displayHint({ title: HelpHints.date_missing, icon: 'default' });
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
            return;
        }

        if (!steps.DATE.internal?.time || steps.DATE?.internal?.time.trim() === '') {
            displayHint({title: HelpHints.slot_missing, icon: 'default'});
            const element = document.getElementById('time-picker');
            const offset = 50; // Adjust this value to your desired offset
            const elementPosition = element.getBoundingClientRect().top + window.scrollY;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            });
            return;
        }

        if (! cartLoading && stepsComplete) {
            createBooking();
        }

    }, [cartLoading, selectionLoading, steps, showHelpNeededHint, clickCount]);

    const helpTimeout = useCallback(() => {
        return setTimeout(() => {
            // Clear any existing timeout to prevent premature closing
            if (hintTimeoutRef.current) {
                clearTimeout(hintTimeoutRef.current);
            }

            // Display the special hint if clicked 3 times rapidly
            // Close the alert if it's currently shown, then reopen it
            setShowHint(false);
            setTimeout(() => {
                setShowHelpNeededHint(true);
                setHint({
                    title: __('book.cart.hints.help_needed'),
                    icon: (
                        <svg className="size-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
                            <rect width="256" height="256" fill="none"/>
                            <circle cx="128" cy="180" r="12"/>
                            <path d="M128,144v-8c17.67,0,32-12.54,32-28s-14.33-28-32-28S96,92.54,96,108v4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>
                            <circle cx="128" cy="128" r="96" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>
                        </svg>
                    ),
                });

                setShowHint(true);
            }, 200);


            // Auto-hide the special hint after 10 seconds
            setTimeout(() => {
                setShowHelpNeededHint(false);
                setShowHint(false);
                setClickCount(0);
            }, 10000);

            return 0; // Reset click count after showing special hint
        }, (parseInt(settings?.ux_defaults?.global_help_popup_timeout ?? 20) * 1000));
    }, [settings?.ux_defaults?.global_help_popup_timeout]);

    useEffect(() => {
        if (currentStep !== STEPS.category) {
            helpHintTimeoutRef.current = helpTimeout();
        }

        return () => {
            if (helpHintTimeoutRef.current) {
                clearTimeout(helpHintTimeoutRef.current);
            }
        };
    }, [currentStep, helpTimeout]);

    const closeEmailVerifiedAlert = useCallback(() => {
        const query = new URLSearchParams(window.location.search);
        query.delete('verified');
        router.visit('/' + query.toString());
    }, []);

    // Scroll listener (for fixed top navigation)
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 28);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const activePromo = useMemo(() => {
        return resolveActivePromo(steps.GAME.internal, games, selected_game);
    }, [steps.GAME.internal, games, selected_game]);

    const hasSpecialPromoCode = !!activePromo;
    const isSummerPromo = activePromo?.code === 'SUMMER30';

    // Automatically creates the booking is all steps are complete
    // useEffect(() => {
    //     if (!cartLoading && stepsComplete) {
    //         createBooking();
    //     }
    // }, [cartLoading, stepsComplete]);

    return (
        <>
            <Head title={__('book.index.meta_title')} />
            <div className="flex flew-col">
                <Container id="main-container" className="flex flex-col justify-between dark:bg-very-dark-gray">
                    <div className="flex flex-col flex-grow">
                        {/* Top nav */}
                        <Transition show={true}>
                            <nav className="fixed w-full w-full-less-aside z-[200] transition flex flex-col">
                                <div className={classNames({
                                    'relative z-10 flex flex-row items-center justify-between w-full dark:w-full-less-aside transition px-3 sm:px-4 lg:px-5': true,
                                    'pb-4 pt-4 sm:pt-5 sm:pb-5 lg:pt-6 lg:pb-6 bg-black lg:bg-very-dark-gray': ! isScrolled,
                                    'pb-3 pt-3 sm:pb-4 sm:pt-4 bg-black-transparent': isScrolled,
                                })}>
                                    <Link href={route('booking.game', {point_of_sale_slug: selected_point_of_sale?.slug ?? null})}>
                                        <ApplicationLogo id="nav-logo" className={classNames({'scrolled': isScrolled})} fill="#fff"/>
                                    </Link>
                                    <div className="flex flex-row gap-4">
                                        {/*<LanguageSwitcher />*/}
                                        <ThemeToggle />
                                        <a
                                            href={`tel:${settings.contact.phone.replace(/\s/g, '')}`}
                                            className={`rounded-md`}
                                            style={{
                                                display: 'inline-flex',
                                                background: 'linear-gradient(to right, rgba(98, 252, 213, 1) 0%, 8.748443337484433%, rgba(106, 245, 229, 1) 17.496886674968867%, 23.941469489414693%, rgba(132, 236, 243, 1) 30.386052303860524%, 43.742216687422165%, rgba(115, 230, 253, 1) 57.098381070983805%, 70.02991951937099%, rgba(170, 249, 173, 1) 82.96145796775818%, 91.48072898387909%, rgba(213, 227, 232, 1) 100%)',
                                                backgroundOrigin: 'border-box',
                                                backgroundClip: 'border-box',
                                                border: 'solid 1.5px transparent',
                                                boxSizing: 'border-box',
                                                opacity: '1',
                                            }}
                                            onClick={() => GTMEventManager.contact('phone', 'top_nav')}
                                        >
                                            <div className="font-semibold inline-flex items-center justify-center w-full text-xs sm:text-sm text-center rounded-md font-space px-2 sm:px-4 py-2 bg-dark-gray text-white tracking-wide transition ease-in-out duration-150 gap-1 sm:gap-2">
                                                <svg className="size-4 sm:size-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
                                                    <rect width="256" height="256" fill="none"/>
                                                    <path d="M152,48a78.61,78.61,0,0,1,56,56" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>
                                                    <path d="M144,80c16.52,4.42,27.58,15.48,32,32" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>
                                                    <path d="M156.39,153.34a8,8,0,0,1,7.59-.69l47.16,21.13a8,8,0,0,1,4.8,8.3A48.33,48.33,0,0,1,168,224,136,136,0,0,1,32,88,48.33,48.33,0,0,1,73.92,40.06a8,8,0,0,1,8.3,4.8l21.13,47.2a8,8,0,0,1-.66,7.53L81.32,125a7.93,7.93,0,0,0-.54,7.81c8.27,16.93,25.77,34.22,42.75,42.41a7.92,7.92,0,0,0,7.83-.59Z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>
                                                </svg>
                                                <span>{settings.contact.phone}</span>
                                            </div>
                                        </a>
                                        {/*{auth.user && (*/}
                                        {/*    <UserMenu user={auth.user}/>*/}
                                        {/*)}*/}
                                        {/*{!auth.user && (*/}
                                        {/*    <Link href={route('login')}>*/}
                                        {/*        <SecondaryButton>Login</SecondaryButton>*/}
                                        {/*    </Link>*/}
                                        {/*)}*/}
                                    </div>
                                </div>
                                {currentStep === STEPS.game && settings.games_alert_content[locale] && settings.games_alert_mobile_content[locale] && (
                                    <Alert primary isScrolled={isScrolled} additionalClasses={{ 'halloween' : true }}>
                                        <span className="text-base 3xl:text-lg hidden lg:block truncate">
                                            {settings.games_alert_content[locale]}
                                        </span>
                                        <span className="text-base lg:hidden truncate">
                                            {settings.games_alert_mobile_content[locale]}
                                        </span>
                                    </Alert>
                                )}
                                {currentStep !== STEPS.location && currentStep !== STEPS.game && activePromo && (
                                    <Alert primary isScrolled={isScrolled}>
                                        {isSummerPromo ? (
                                            <>
                                                <span className="text-base 3xl:text-lg hidden lg:flex items-center gap-2 truncate">
                                                    <BeachUmbrellaIcon className="size-5 shrink-0" aria-hidden="true" />
                                                    <span className="truncate">{__('book.promo.alert_summer', { discount: activePromo.discount_label })}</span>
                                                </span>
                                                <span className="text-base lg:hidden flex items-center gap-1.5 truncate">
                                                    <BeachUmbrellaIcon className="size-4 shrink-0" aria-hidden="true" />
                                                    <span className="truncate">{__('book.promo.alert_summer', { discount: activePromo.discount_label })}</span>
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="text-base 3xl:text-lg hidden lg:flex truncate">
                                                    <span>{__('book.promo.alert_desktop', { discount: activePromo.discount_label })} <strong className="tracking-wide">{activePromo.code}</strong>{activePromo.expired_at ? <>&nbsp;-&nbsp;</> : null}</span>
                                                    {activePromo.expired_at && (
                                                        <AlertCountdown
                                                            targetTime={endOfDay(new Date())}
                                                            onCountdownFinished={() => {}}
                                                        />
                                                    )}
                                                </span>
                                                <span className="flex text-base lg:hidden truncate">
                                                    <span>{__('book.promo.alert_mobile', { discount: activePromo.discount_label })} <strong className="tracking-wide">{activePromo.code}</strong>{activePromo.expired_at ? <>&nbsp;-&nbsp;</> : null}</span>
                                                    {activePromo.expired_at && (
                                                        <AlertCountdown
                                                            targetTime={endOfDay(new Date())}
                                                            onCountdownFinished={() => {}}
                                                        />
                                                    )}
                                                </span>
                                            </>
                                        )}
                                    </Alert>
                                )}
                                <Transition
                                    as={Fragment}
                                    show={! isScrolled}
                                    enter="ease-out transition transform duration-400"
                                    enterFrom="opacity-50 -translate-y-4"
                                    enterTo="opacity-100 translate-y-0"
                                    leave="ease-in transition transform duration-0"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <div className={`flex flex-row justify-between items-center pt-5 sm:pt-8 pb-3 gap-4 px-3.5 sm:px-5 lg:px-6 dark:bg-black dark:lg:bg-transparent`}>
                                        <div className="flex flex-row items-center justify-center gap-2 sm:gap-4 overflow-hidden">
                                            {/*{currentStep === 'GAME' && (*/}
                                            {/*    <div*/}
                                            {/*        className="flex items-center justify-center rounded-full cursor-pointer bg-gray-915 hover:bg-dark-gray transition duration-200"*/}
                                            {/*        onClick={() => {*/}
                                            {/*            setShowHelpNeededHint(false);*/}
                                            {/*            setShowHint(false);*/}
                                            {/*            setClickCount(0);*/}
                                            {/*            if (helpHintTimeoutRef.current) {*/}
                                            {/*                clearTimeout(helpHintTimeoutRef.current);*/}
                                            {/*            }*/}
                                            {/*            setCurrentStep('CATEGORY');*/}
                                            {/*        }}*/}
                                            {/*    >*/}
                                            {/*        <span className="flex flex-row items-center justify-center gap-1 text-xxs text-lighter-gray uppercase px-2 pr-4 py-1.5 leading-none">*/}
                                            {/*            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">*/}
                                            {/*              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>*/}
                                            {/*            </svg>*/}
                                            {/*            {__('book.index.steps.game.back_categories')}*/}
                                            {/*        </span>*/}
                                            {/*    </div>*/}
                                            {/*)}*/}
                                            <h4 className="dark:text-white font-semibold whitespace-nowrap text-ellipsis truncate text-lg sm:text-xl 3xl:text-xl">
                                                {steps[currentStep].title}
                                            </h4>
                                        </div>
                                        <div className="flex flex-row gap-1">
                                            {Object.keys(steps).filter(s => s !== 'CATEGORY').map((step) => (
                                                <span
                                                    key={step}
                                                    className={classNames({
                                                        'border border-medium-gray w-2.5 h-2.5 lg:w-3 lg:h-3': true,
                                                        'bg-white': currentStep === step,
                                                        'bg-gray-700': currentStep !== step && steps[step].internal === null,
                                                        'bg-primary': currentStep !== step && steps[step].internal !== null,
                                                    })}
                                                ></span>
                                            ))}
                                        </div>
                                    </div>
                                </Transition>
                            </nav>
                        </Transition>

                        <div id="steps-parent" className={classNames({
                            'flex': true,
                            'flex-grow': currentStep === STEPS.game,
                        })}>
                            {/* Current step */}
                            <div id="steps-container" className={classNames({
                                'flex-grow': true,
                                'with-alert': (settings.games_alert_content[locale] && settings.games_alert_mobile_content[locale] && currentStep === STEPS.game) || (hasSpecialPromoCode && currentStep !== STEPS.location && currentStep !== STEPS.game),
                            })}>
                                {currentStep === STEPS.location && (
                                    <LocationStep
                                        pointsOfSale={points_of_sale}
                                        selected={selected_point_of_sale}
                                        settings={settings}
                                        onSelectPointOfSale={submitStep}
                                    />
                                )}

                                {currentStep === STEPS.game && (
                                    <GameStep
                                        games={games}
                                        settings={settings}
                                        loading={gamesLoading}
                                        loaded={gamesLoaded}
                                        categories={categories}
                                        onSelectGame={submitStep}
                                    />
                                )}

                                {currentStep === STEPS.team && (
                                    <TeamStep
                                        settings={settings}
                                        min={steps.GAME.internal?.min_players ?? selected_game?.min_players ?? 1}
                                        max={steps.GAME.internal?.max_players ?? selected_game?.max_players ?? maximum_players}
                                        onSelectTeam={submitStep}
                                    />
                                )}

                                {selectionError && <p role="alert">{__('book.alternatives.selection_error')}</p>}
                                {currentStep === STEPS.date && selectionLoading && <p role="status">{__('book.alternatives.loading')}</p>}
                                {currentStep === STEPS.date && !selectionLoading && selected_game && steps.GAME.internal?.id === selected_game.id && (
                                    <DateStep
                                        key={`${selected_point_of_sale.id}:${selected_game.id}:${steps.TEAM.internal}:${selected_slot?.date ?? ''}:${selected_slot?.time ?? ''}`}
                                        game={steps.GAME.internal}
                                        games={games}
                                        locale={locale}
                                        onSelectAlternative={selectAlternative}
                                        settings={settings}
                                        slots={slots}
                                        maxDays={planning_days}
                                        openingHours={selected_point_of_sale.opening_hours}
                                        pricingExceptions={pricing_exceptions ?? {}}
                                        initialDate={steps.DATE.internal}
                                        team={steps.TEAM.internal}
                                        selectedPointOfSale={selected_point_of_sale}
                                        onSelectDate={submitStep}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </Container>

                <Summary id="main-summary" className={classNames({
                    'hidden lg:flex flex-col h-full dark:min-h-screen dark:justify-between dark:fixed z-30 dark:right-0 dark:bg-medium-gray bg-white dark:text-white overflow-auto mr-5 border border-black dark:mr-0 dark:border-0': true,
                    'is-scrolled': isScrolled,
                })}>
                    <div className="flex flex-col dark:mb-[200px]">
                        <h2 className="text-md 2xl:text-lg 3xl:text-xl font-semibold py-4 dark:py-6 px-6 mt-2">{__('book.common.summary')}</h2>
                        <AsideMenu steps={steps} currentStep={currentStep} setCurrentStep={(step) => {
                            // if (step === 'GAME' && steps.GAME.internal) {
                            //     setSelectedCategory(null);
                            // }
                            setCurrentStep(step)
                        }} />
                    </div>
                    <div className="flex flex-col flex-grow pb-8 px-4 3xl:px-6 dark:fixed dark:bg-medium-gray z-50 dark:bottom-0 continue-container">
                        {steps.GAME.internal && steps.TEAM.internal && steps.DATE?.internal?.slot && (
                            <div className="flex flex-row justify-between items-center mb-4 dark:border-t border-light-gray dark:pt-4">
                                <span className="dark:text-light-gray text-lg sm:text-lg 2xl:text-xl font-semibold">
                                    {__('book.common.total')}
                                </span>
                                {steps.DATE.internal?.slot?.price && ! isNaN((steps.DATE.internal.slot.price) * steps.TEAM.internal) && (
                                    <span className="dark:text-white text-lg sm:text-xl 3xl:text-2xl font-bold font-space">
                                        {numbro(((steps.DATE.internal.slot.price) * steps.TEAM.internal) / 100).formatCurrency({ mantissa: 2 })}
                                    </span>
                                )}
                            </div>
                        )}
                        <PrimaryButton
                            className="border border-gray-300 w-full text-center"
                            disabled={cartLoading || ! stepsComplete}
                            onClick={createBooking}
                        >
                            {__('book.common.cta.continue')}
                        </PrimaryButton>
                    </div>
                </Summary>
                {currentStep !== STEPS.category && (
                    <SummaryMobile className="bg-black z-50 px-4 py-3 lg:hidden">
                        <div className="flex flex-col gap-3">
                            {/*{currentStep === STEPS.date && (*/}
                            {/*    <div className="flex flex-row justify-between xl:hidden items-center text-white ">*/}
                            {/*        <div className="flex flex-row items-center gap-2">*/}
                            {/*            <span className="text-lg">*/}
                            {/*                {__('book.common.total')}:*/}
                            {/*            </span>*/}
                            {/*            {steps.DATE.internal?.slot?.price && (*/}
                            {/*                <div className="flex flex-row items-center">*/}
                            {/*                    <div className="text-base flex flex-row items-center gap-2 bg-gray-915 rounded py-0.5 px-2 cursor-pointer" onClick={() => setCurrentStep(STEPS.team)}>*/}
                            {/*                        <span>{steps.TEAM.internal} {steps.TEAM.internal <= 1 ? __('book.common.player') : __('book.common.players')}</span>*/}
                            {/*                        <svg className="size-4 text-lighter-gray" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">*/}
                            {/*                            <rect width="256" height="256" fill="none"/>*/}
                            {/*                            <path d="M92.69,216H48a8,8,0,0,1-8-8V163.31a8,8,0,0,1,2.34-5.65L165.66,34.34a8,8,0,0,1,11.31,0L221.66,79a8,8,0,0,1,0,11.31L98.34,213.66A8,8,0,0,1,92.69,216Z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>*/}
                            {/*                            <line x1="136" y1="64" x2="192" y2="120" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="16"/>*/}
                            {/*                        </svg>*/}
                            {/*                    </div>*/}
                            {/*                    <span>&nbsp;x {numbro(steps.DATE.internal.slot.price / 100).formatCurrency({mantissa: 2})}</span>*/}
                            {/*                </div>*/}
                            {/*            )}*/}
                            {/*        </div>*/}
                            {/*        {steps.DATE.internal?.slot?.price && !isNaN((steps.DATE.internal.slot.price) * steps.TEAM.internal) && (*/}
                            {/*            <span className="text-lg sm:text-xl font-bold font-space">*/}
                            {/*                {numbro(((steps.DATE.internal.slot.price) * steps.TEAM.internal) / 100).formatCurrency({ mantissa: 2 })}*/}
                            {/*            </span>*/}
                            {/*        )}*/}
                            {/*        {(! steps.DATE.internal?.slot?.price || isNaN((steps.DATE.internal.slot.price) * steps.TEAM.internal)) && (*/}
                            {/*            <span className="text-lg sm:text-xl font-bold font-space">*/}
                            {/*                — €*/}
                            {/*            </span>*/}
                            {/*        )}*/}
                            {/*    </div>*/}
                            {/*)}*/}

                            <PrimaryButton className="border border-gray-300 w-full text-center" onClick={handleClickContinue} disabled={cartLoading || selectionLoading}>
                                {!cartLoading && (<span>{__('book.common.cta.continue')}</span>)}
                                {cartLoading && (
                                    <div className="flex items-center justify-center">
                                        <div className="relative size-6" style={{ height: '23px' }}>
                                            <div className="size-6 rounded-full absolute border border-solid" style={{ borderColor: 'rgba(0, 0, 0, 0.25)' }}></div>
                                            <div className="size-6 rounded-full animate-spin absolute border border-solid border-black border-t-transparent"></div>
                                        </div>
                                    </div>
                                )}
                            </PrimaryButton>
                        </div>
                    </SummaryMobile>
                )}
            </div>

            <Footer />

            <HelpPopup
                className={classNames({
                    'fixed flex justify-end lg:hidden w-[90%] z-[100]': true,
                    'right-3 sm:right-6 bottom-[5.25rem] sm:bottom-[6rem]': currentStep !== STEPS.date,
                    'right-3 sm:right-6 bottom-32 sm:bottom-36': currentStep === STEPS.date,
                })}
                showHint={showHint}
                showHelpNeededHint={showHelpNeededHint}
                hint={hint}
            />

            {/*{currentStep === STEPS.game && (*/}
            {/*    <DartsPromoPopup*/}
            {/*        visible={showDartsPromo}*/}
            {/*        onDismiss={() => setShowDartsPromo(false)}*/}
            {/*    />*/}
            {/*)}*/}

            <Transition appear show={verifyingEmail} as={Fragment}>
                <div className="fixed left-3 bottom-[5.5rem] lg:left-8 lg:bottom-8 w-[90%] z-[100]"
                     onClose={closeEmailVerifiedAlert}>
                    <div className="flex min-h-full">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-400"
                            enterFrom="opacity-0 scale-80 -translate-x-4 translate-y-12"
                            enterTo="opacity-100 scale-100 translate-x-0 translate-y-0"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100 translate-x-0 translate-y-0"
                            leaveTo="opacity-0 scale-80 -translate-x-4 translate-y-12"
                        >
                            <div
                                className="w-full max-w-md p-3 transform overflow-hidden rounded-xl lg:rounded-2xl bg-light-gray text-white lg:p-6 text-left align-middle shadow-2xl border border-light-gray transition-all">
                                <div className="flex flex-row items-center gap-1.5 lg:gap-2">
                                    <CheckIconSvg className="size-5 lg:size-6 text-green-600"/>
                                    <h3 className="text-base lg:text-lg font-medium leading-6 text-gray-800">
                                        {__('book.email_verification.title')}
                                    </h3>
                                </div>
                                <div className="mt-2 lg:mt-4">
                                    <p className="text-xs lg:text-sm text-gray-600">{__('book.email_verification.body')}</p>
                                </div>

                                <div className="mt-3 lg:mt-5">
                                    <ActionButton onClick={closeEmailVerifiedAlert} style={{'background': '#1A1A1A'}}>
                                        {__('book.email_verification.close_button')}
                                    </ActionButton>
                                </div>
                            </div>
                        </Transition.Child>
                    </div>
                </div>
            </Transition>
        </>
    );
}

export default BookingIndex;
